import { toast } from "@/lib/toast";
import { apiClient } from "@/services/api";
import type { BeancountSyncConfig, BeancountSyncPlan, SyncEntityType } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { dashboardKeys } from "@/pages/dashboard/hooks/useDashboard";
import { accountKeys } from "./useAccounts";
import { categoryKeys } from "./useCategories";
import { transactionKeys } from "./useTransactions";

export const beancountKeys = {
  all: ["beancount"] as const,
  config: () => [...beancountKeys.all, "config"] as const,
  status: () => [...beancountKeys.all, "status"] as const,
  ledger: () => [...beancountKeys.all, "ledger"] as const,
};

export function useBeancountConfig() {
  return useQuery({
    queryKey: beancountKeys.config(),
    queryFn: () => apiClient.getBeancountConfig(),
  });
}

export function useBeancountStatus() {
  return useQuery({
    queryKey: beancountKeys.status(),
    queryFn: () => apiClient.getBeancountStatus(),
    refetchInterval: 30_000,
  });
}

export function useUpdateBeancountConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      patch: Partial<Omit<BeancountSyncConfig, "git">> & {
        git?: { remoteUrl: string; branch: string } | null;
      },
    ) => apiClient.updateBeancountConfig(patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: beancountKeys.all });
      toast({ title: "Success", description: "Beancount settings saved" });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to save settings",
        variant: "destructive",
      });
    },
  });
}

export function usePreviewBeancountSync() {
  return useMutation({
    mutationFn: (entityTypes?: SyncEntityType[]) => apiClient.previewBeancountSync(entityTypes),
  });
}

export function useRunBeancountSync() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (entityTypes?: SyncEntityType[]) => apiClient.runBeancountSync(entityTypes),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: beancountKeys.all });
      const queued = result as { queued?: boolean; alreadyRunning?: boolean };
      if (queued.alreadyRunning) {
        toast({
          title: "Sync already running",
          description: "A beancount sync is still in progress — it will finish shortly.",
        });
        return;
      }
      if (queued.queued === true) {
        // Sync runs as a background job (memory-heavy, kept off the web dyno).
        toast({
          title: "Sync queued",
          description: "Running in the background — check back in a moment for results.",
        });
        // Poll status while the job runs
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: beancountKeys.status() });
        }, 4000);
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: beancountKeys.status() });
          queryClient.invalidateQueries({ queryKey: transactionKeys.all });
          queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
        }, 12000);
        return;
      }
      // Legacy inline plan (dryRun) — show the summary
      const s = (result as BeancountSyncPlan).summary;
      queryClient.invalidateQueries({ queryKey: transactionKeys.all });
      queryClient.invalidateQueries({ queryKey: accountKeys.all });
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
      queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
      toast({
        title: "Sync complete",
        description: `${s.pushUpdates} pushed · ${s.pullUpdates} pulled · ${s.appCreates} imported · ${s.conflicts} conflicts`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Sync failed",
        description: error.message || "Beancount sync failed",
        variant: "destructive",
      });
    },
  });
}

export function useResetBeancountState() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.resetBeancountState(),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: beancountKeys.all });
      toast({
        title: "Sync state reset",
        description: `${result.deleted} mapping rows deleted. Next sync will re-link everything.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to reset sync state",
        variant: "destructive",
      });
    },
  });
}
